import { Briefcase, ChevronDown, Globe2, GraduationCap, Layers, MapPin } from "lucide-react";
import { useMemo, useState } from "react";

import ProfileAvatar from "../../../components/ProfileAvatar";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../handlers/cn.utils";
import { capitalizeFirstLetterOfWords } from "../../../handlers/utilityFunctions";

interface JobPreferences {
	desiredTitle?: string;
	currentJobsearchStatus?: string;
	areasOfExpertise?: string[] | string;
	seniorityLevel?: string;
	openForWorkTypes?: string[] | string;
	locationPreferenceType?: string;
	skills?: string[] | string;
	address?: { city?: string; region?: string; country?: string };
}

interface CompletenessDetail {
	key: string;
	isComplete: boolean;
}

interface ProfileSummaryCardProps {
	firstName?: string;
	lastName?: string;
	picture?: string;
	jobPreferences?: JobPreferences;
	completenessDetails?: CompletenessDetail[];
	completenessPct: number;
	onEditPreferences: () => void;
}

/**
 * Our own wording for each preference key. The API ships a `message` for every one of these, but
 * those strings are misspelled in production ("Add super-power skilss", "seniority or experperince
 * level", "open to work anywehere") — so they are never rendered.
 */
const PREFERENCE_LABELS: Record<string, string> = {
	currentJobsearchStatus: "job search status",
	waysToHelpme: "how we can help",
	primaryResumeReferenceId: "a linked resume",
	openForWorkTypes: "work types",
	seniorityLevel: "seniority level",
	areasOfExpertise: "areas of expertise",
	locationPreferenceType: "relocation preference",
	locationsPreferred: "preferred locations",
	skills: "skills",
	address: "your location",
	recentGraduate: "graduate status",
	graduationDate: "graduation date",
	professionalSince: "career start date",
	educationLevels: "education level",
	desiredTitle: "the role you want",
};

/** Enough to show the shape of someone's skill set; the rest sits behind the count. */
const SKILLS_SHOWN = 8;

// Some accounts store these as a comma-joined string rather than an array.
function toArray(value: unknown): string[] {
	if (Array.isArray(value)) return value.map((entry) => String(entry).trim()).filter(Boolean);

	if (typeof value !== "string") return [];

	return value
		.split(",")
		.map((entry) => entry.trim())
		.filter(Boolean);
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
	return (
		<div className="flex items-start gap-3">
			<div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-500">{icon}</div>
			<div className="min-w-0">
				<p className="text-xs text-gray-500 font-inter">{label}</p>
				<p className="text-sm font-medium text-gray-900 font-inter">{value}</p>
			</div>
		</div>
	);
}

/**
 * The rail summary of who Dproz thinks you are. Everything here is an input to the picks in the main
 * column, so the one action is "Edit preferences" and the strength meter names what is still missing
 * rather than only showing a percentage.
 */
function ProfileSummaryCard({
	firstName,
	lastName,
	picture,
	jobPreferences,
	completenessDetails,
	completenessPct,
	onEditPreferences,
}: ProfileSummaryCardProps) {
	const preferences = jobPreferences ?? {};
	const name = capitalizeFirstLetterOfWords([firstName, lastName].filter(Boolean).join(" ") || "there");

	const skills = useMemo(() => toArray(preferences.skills), [preferences.skills]);
	const workTypes = useMemo(() => toArray(preferences.openForWorkTypes), [preferences.openForWorkTypes]);
	const expertise = useMemo(() => toArray(preferences.areasOfExpertise), [preferences.areasOfExpertise]);

	const missing = useMemo(
		() =>
			(completenessDetails ?? [])
				.filter((detail) => !detail.isComplete)
				.map((detail) => PREFERENCE_LABELS[detail.key])
				.filter(Boolean),
		[completenessDetails],
	);

	const place = [preferences.address?.city, preferences.address?.country].filter(Boolean).join(", ");
	const isOpen = Boolean(preferences.currentJobsearchStatus);
	const [isExpanded, setIsExpanded] = useState(false);

	const details = [
		expertise.length > 0 && {
			icon: <Layers className="size-4" />,
			label: "Areas of expertise",
			value: expertise.join(", "),
		},
		preferences.seniorityLevel && {
			icon: <GraduationCap className="size-4" />,
			label: "Seniority",
			value: preferences.seniorityLevel,
		},
		workTypes.length > 0 && {
			icon: <Briefcase className="size-4" />,
			label: "Open to",
			value: workTypes.join(", "),
		},
		place && { icon: <MapPin className="size-4" />, label: "Based in", value: place },
		preferences.locationPreferenceType && {
			icon: <Globe2 className="size-4" />,
			label: "Location preference",
			value: preferences.locationPreferenceType,
		},
	].filter(Boolean) as { icon: React.ReactNode; label: string; value: string }[];

	// A brand-new account has no details and no skills, so the collapsed card would be the whole card
	// and the toggle would open nothing but the Edit button.
	const hasCollapsibleContent = details.length > 0 || skills.length > 0;

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6">
			<div className="flex flex-col items-center text-center">
				<ProfileAvatar
					src={picture}
					name={name}
					className="size-20 rounded-full object-cover border border-gray-200"
					width={80}
					height={80}
				/>
				<h2 className="mt-3 text-base font-bold text-gray-900 font-plus-jakarta">{name}</h2>
				{preferences.desiredTitle && (
					<p className="mt-0.5 text-sm text-gray-500 font-inter">{preferences.desiredTitle}</p>
				)}
				{isOpen && (
					<span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
						<span className="size-1.5 rounded-full bg-green-600" />
						{preferences.currentJobsearchStatus}
					</span>
				)}
			</div>

			<div className="mt-6 pt-5 border-t border-gray-100">
				<div className="flex items-center justify-between">
					<p className="text-sm font-medium text-gray-900 font-inter">Profile strength</p>
					<p className="text-sm font-semibold text-primary-700 tabular-nums">{completenessPct}%</p>
				</div>
				<div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
					<div
						className="h-full rounded-full bg-primary-700 origin-left transition-transform duration-700 ease-out motion-reduce:transition-none"
						style={{ transform: `scaleX(${Math.max(0, Math.min(completenessPct, 100)) / 100})` }}
						role="progressbar"
						aria-valuenow={completenessPct}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-label="Profile strength"
					/>
				</div>
				{/* A bare percentage never says what to do next, so name the fields behind it. */}
				{missing.length > 0 && (
					<p className="mt-2 text-xs text-gray-500 font-inter">
						Add {missing.slice(0, 2).join(" and ")}
						{missing.length > 2 && ` and ${missing.length - 2} more`} for sharper matches.
					</p>
				)}
			</div>

			{/* On a phone this card sits above the job picks, so everything past the strength meter is
			    collapsed by default — otherwise the picks start a full screen down. On desktop it is
			    a sidebar with room to spare, so it is always open and the toggle never renders. */}
			<div className={cn("lg:block", isExpanded || !hasCollapsibleContent ? "block" : "hidden")}>
				{details.length > 0 && (
					<div className="mt-5 pt-5 border-t border-gray-100 space-y-4">
						{details.map((detail) => (
							<DetailRow key={detail.label} icon={detail.icon} label={detail.label} value={detail.value} />
						))}
					</div>
				)}

				{skills.length > 0 && (
					<div className="mt-5 pt-5 border-t border-gray-100">
						<div className="flex items-center justify-between mb-3">
							<p className="text-sm font-medium text-gray-900 font-inter">Skills</p>
							<p className="text-xs text-gray-500 font-inter tabular-nums">{skills.length} total</p>
						</div>
						<div className="flex flex-wrap gap-1.5">
							{skills.slice(0, SKILLS_SHOWN).map((skill) => (
								<span
									key={skill}
									className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 font-inter"
								>
									{skill}
								</span>
							))}
							{skills.length > SKILLS_SHOWN && (
								<button
									type="button"
									onClick={onEditPreferences}
									className="rounded-md px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 font-inter"
								>
									+{skills.length - SKILLS_SHOWN} more
								</button>
							)}
						</div>
					</div>
				)}

				<Button variant="outlineSecondary" className="w-full mt-6" onClick={onEditPreferences}>
					Edit preferences
				</Button>
			</div>

			{hasCollapsibleContent && (
				<button
					type="button"
					onClick={() => setIsExpanded((open) => !open)}
					aria-expanded={isExpanded}
					className="lg:hidden mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-primary-700"
				>
					{isExpanded ? "Hide profile details" : "Show profile details"}
					<ChevronDown
						className={cn(
							"size-4 transition-transform duration-200 motion-reduce:transition-none",
							isExpanded && "rotate-180",
						)}
					/>
				</button>
			)}
		</div>
	);
}

export default ProfileSummaryCard;
